// What the dashboard has read from the server, shared by its pages.
//
// The rail counts the shops, cashiers, tills and discounts, Home and Orders read every
// shop's day, and the menu page needs the shops for its price columns. Each
// is read once when first wanted and again after a change, so moving between
// pages does not refetch what is already on screen.
//
// A read that fails keeps what was there and says so through `failure`,
// which carries the server's own words when it sent some.

import { api } from '../api/client';
import type { components } from '../api/types';
import { forgetDraft, loadDraft } from './draft.svelte';
import type { Refusal } from './tills';

export type Shop = components['schemas']['OwnerShopBody'];
export type Cashier = components['schemas']['CashierBody'];
export type Till = components['schemas']['TillBody'];
export type Menu = components['schemas']['MenuBody'];
export type Day = components['schemas']['DayBody'];
export type Discount = components['schemas']['DiscountBody'];

interface Loaded<T> {
	value: T;
	loaded: boolean;
	failure: string;
	/** The failure was the network's or the server's (5xx), so Try again may help (web.md, Errors). */
	retryable: boolean;
}

function empty<T>(value: T): Loaded<T> {
	return { value, loaded: false, failure: '', retryable: false };
}

const shops = $state(empty<Shop[]>([]));
const cashiers = $state(empty<Cashier[]>([]));
const tills = $state(empty<Till[]>([]));
const discounts = $state(empty<Discount[]>([]));
// Whether a discount is open for editing, so the title row's "New discount"
// waits until it is saved or cancelled, as D21 draws it.
let discountFormOpen = $state(false);
const menu = $state(
	empty<Menu>({ categories: [], groups: [], items: [], discounts: [], webBuild: '' }),
);
const days = $state<Record<string, Day | null>>({});
let daysLoaded = $state(false);

export function ownerShops(): Loaded<Shop[]> {
	return shops;
}

export function ownerCashiers(): Loaded<Cashier[]> {
	return cashiers;
}

export function ownerTills(): Loaded<Till[]> {
	return tills;
}

// A revoke runs when its Undo runs out, so the server can refuse it after
// Tills has unmounted: the refusal waits here, by till, until the next
// revoke of that till.
const refusals = $state<Record<string, Refusal>>({});

export function tillRefusals(): Record<string, Refusal> {
	return refusals;
}

export function setTillRefusal(id: string, refusal: Refusal): void {
	refusals[id] = refusal;
}

export function clearTillRefusal(id: string): void {
	delete refusals[id];
}

export function ownerDiscounts(): Loaded<Discount[]> {
	return discounts;
}

export function discountEditing(): boolean {
	return discountFormOpen;
}

export function setDiscountEditing(open: boolean): void {
	discountFormOpen = open;
}

export function ownerMenu(): Loaded<Menu> {
	return menu;
}

/** Today's orders and summary per shop, keyed by shop id. */
export function ownerDays(): { days: Record<string, Day | null>; loaded: boolean } {
	return { days, loaded: daysLoaded };
}

type Fetched<T> = { data?: T; error?: { message?: string }; response: Response };

async function read<T>(into: Loaded<T>, fetch: () => Promise<Fetched<T>>, fallback: string) {
	try {
		const { data, error, response } = await fetch();
		if (error !== undefined || data === undefined) {
			into.failure = error?.message ?? fallback;
			into.retryable = response.status >= 500;
			return;
		}
		into.value = data;
		into.failure = '';
		into.retryable = false;
	} catch {
		into.failure = fallback;
		into.retryable = true;
	} finally {
		into.loaded = true;
	}
}

export async function loadShops(fallback: string): Promise<void> {
	await read(
		shops,
		async () => {
			const { data, error, response } = await api.GET('/api/owner/shops');
			return { data: data ? (data.shops ?? []) : undefined, error, response };
		},
		fallback,
	);
}

export async function loadCashiers(fallback: string): Promise<void> {
	await read(
		cashiers,
		async () => {
			const { data, error, response } = await api.GET('/api/owner/cashiers');
			return { data: data ? (data.cashiers ?? []) : undefined, error, response };
		},
		fallback,
	);
}

export async function loadTills(fallback: string): Promise<void> {
	await read(
		tills,
		async () => {
			const { data, error, response } = await api.GET('/api/owner/tills');
			return { data: data ? (data.tills ?? []) : undefined, error, response };
		},
		fallback,
	);
}

export async function loadDiscounts(fallback: string): Promise<void> {
	await read(
		discounts,
		async () => {
			const { data, error, response } = await api.GET('/api/owner/discounts');
			return { data: data ? (data.discounts ?? []) : undefined, error, response };
		},
		fallback,
	);
	// A discount save waits in the menu draft (#349): the draft bar counts it.
	await loadDraft(fallback);
}

export async function loadMenu(fallback: string): Promise<void> {
	// This bundle reads items sold by weight (UC-9.R14).
	await read(
		menu,
		() => api.GET('/api/owner/menu', { params: { query: { weighed: true, perKg: true } } }),
		fallback,
	);
	// Every menu and option save reads the menu again, and waits in the
	// draft (#349): the draft bar counts it.
	await loadDraft(fallback);
}

/** Every open shop's day, one read each, all at once. */
export async function loadDays(): Promise<void> {
	const read = await Promise.all(
		shops.value.map(async (shop) => {
			try {
				const { data } = await api.GET('/api/owner/orders', {
					// Weighed lines whole, with their grams (UC-12.R13).
					params: { query: { shopId: shop.id, weighed: true } },
				});
				return [shop.id, data ?? null] as const;
			} catch {
				return [shop.id, null] as const;
			}
		}),
	);
	for (const [id, day] of read) days[id] = day;
	daysLoaded = true;
}

/** Forget everything, as signing out must. */
export function forgetOwnerData(): void {
	for (const id of Object.keys(refusals)) clearTillRefusal(id);
	Object.assign(shops, empty<Shop[]>([]));
	Object.assign(cashiers, empty<Cashier[]>([]));
	Object.assign(tills, empty<Till[]>([]));
	Object.assign(discounts, empty<Discount[]>([]));
	Object.assign(
		menu,
		empty<Menu>({ categories: [], groups: [], items: [], discounts: [], webBuild: '' }),
	);
	for (const key of Object.keys(days)) delete days[key];
	daysLoaded = false;
	forgetDraft();
}

// Which shop the Orders page shows. Home's "Shop orders" sets it before it
// goes there, so the page opens on the shop the owner was looking at.
let ordersShop = $state('all');

export function ordersShopFilter(): string {
	return ordersShop;
}

export function setOrdersShop(id: string): void {
	ordersShop = id;
}
